import { getAskVolumeField, getBidVolumeField, getFieldValue } from '../utils/dataModeUtils';

export type DataModeLocal = 'accumulated' | '5min';

export const toNumber = (v: any, def: number = 0): number => {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return isNaN(n) ? def : n;
};

export const normalizeSide = (item: any): 'C' | 'P' | '' => {
  const candidates: any[] = [
    item?.['Option Type'],
    item?.optionType,
    item?.option_type,
    item?.Side,
    item?.side,
    item?.Type,
    item?.type
  ];
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    const raw = String(c).toUpperCase().trim();
    if (raw === 'CALL' || raw === 'CALLS' || raw === 'C') return 'C';
    if (raw === 'PUT' || raw === 'PUTS' || raw === 'P') return 'P';
  }
  return '' as const;
};

export const getStrikeValue = (item: any, dataMode: DataModeLocal): number => {
  // Try common variations via getFieldValue first
  const primary = getFieldValue(item, 'Strike', dataMode as any);
  const fallbacks = [
    item?.Strike,
    item?.strike,
    item?.['Strike Price'],
    item?.['StrikePrice'],
    item?.['STRIKE'],
    item?.['Option Strike'],
    item?.['option_strike'],
    item?.['strike_price']
  ];
  const candidates = [primary, ...fallbacks];
  for (const c of candidates) {
    const num = toNumber(c, NaN);
    if (!isNaN(num)) return num;
  }
  return NaN;
};

export const getUnderlyingPriceValue = (item: any, dataMode: DataModeLocal): number => {
  const v = getFieldValue(item, 'Underlying Price', dataMode as any) ?? item?.UnderlyingPrice ?? item?.underlying_price;
  const n = toNumber(v, 0);
  return n > 0 ? n : 100; // sensible default
};

export const getDeltaProxy = (item: any, dataMode: DataModeLocal): number => {
  // 1) Try explicit Delta
  const delta = toNumber(getFieldValue(item, 'Delta', dataMode as any), NaN);
  if (!isNaN(delta) && delta !== 0) return delta;

  // 2) Fallback to Direction if present
  const direction = toNumber(getFieldValue(item, 'Direction', dataMode as any), NaN);
  if (!isNaN(direction) && direction !== 0) return Math.max(-1, Math.min(1, direction));

  // 3) Fallback to ask-bid ratio
  const bidField = getBidVolumeField(dataMode as any);
  const askField = getAskVolumeField(dataMode as any);
  const bid = toNumber(item?.[bidField] ?? getFieldValue(item, 'Bid Volume', dataMode as any), 0);
  const ask = toNumber(item?.[askField] ?? getFieldValue(item, 'Ask Volume', dataMode as any), 0);
  const total = bid + ask;
  if (total > 0) return (ask - bid) / total; // -1..+1

  // 4) Last resort zero
  return 0;
};

export const getGammaOrProxy = (item: any, dataMode: DataModeLocal, precomputedDelta?: number): number => {
  // 1) Try explicit Gamma
  const gamma = toNumber(getFieldValue(item, 'Gamma', dataMode as any) ?? (item?.Gamma ?? item?.gamma), NaN);
  if (!isNaN(gamma) && gamma !== 0) return gamma;

  // 2) Simple proxy from delta magnitude
  const delta = precomputedDelta ?? getDeltaProxy(item, dataMode);
  const approx = Math.abs(delta) * 0.02; // tuned small proxy so charts render
  return approx;
};

export const getStrikeKeyWithFallback = (
  item: any,
  dataMode: DataModeLocal,
  counter: { value: number }
): string => {
  const v = getStrikeValue(item, dataMode);
  if (!isNaN(v)) return String(v);
  counter.value += 1;
  return String(counter.value);
};

const GREEK_NEST_BUCKETS = ['second_order', 'third_order', 'greeks', 'first_order', 'data', 'response'] as const;

/**
 * Per-contract Greek: top-level (e.g. Vanna) then Theta nested buckets (`second_order`, …).
 * Theta `option/snapshot/greeks/all` bulk rows sometimes carry vanna/charm only under `second_order` until merge/flatten.
 */
export function readPerLegGreek(row: Record<string, unknown>, titleKey: string, snakeKey: string): number {
  const topRaw = (row as any)[titleKey] ?? (row as any)[snakeKey] ?? (row as any)[snakeKey.toUpperCase()];
  if (topRaw !== undefined && topRaw !== null && topRaw !== '') {
    const top = toNumber(topRaw, NaN);
    if (!Number.isNaN(top)) return top;
  }
  const titleSnake = titleKey.replace(/\s+/g, '_').toLowerCase();
  for (const nest of GREEK_NEST_BUCKETS) {
    const blob = row[nest];
    if (blob && typeof blob === 'object' && !Array.isArray(blob)) {
      const b = blob as Record<string, unknown>;
      const cap = snakeKey.charAt(0).toUpperCase() + snakeKey.slice(1);
      const v = toNumber(
        (b[titleKey] ??
          b[snakeKey] ??
          b[snakeKey.toUpperCase()] ??
          b[cap] ??
          b[titleSnake]) as string | number | undefined,
        NaN,
      );
      if (!Number.isNaN(v)) return v;
    }
  }
  return 0;
} 